import React, { ComponentType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SECTIONS, NavSection } from '../../config/navigation';
import { itemsOn } from '../../config/navigationModules';
import { useMemberScope } from '../../hooks/useMemberScope';
import { useCanOpen } from '../../hooks/useMyAccess';
import { useSwitchedOffModules } from '../../hooks/useSwitchedOffModules';
import { useActiveSection } from '../../hooks/useActiveSection';
import { NotesSidebar } from '../notes/NotesSidebar';

const CUSTOM_SIDEBARS: Partial<Record<NavSection, ComponentType>> = {
  notes: NotesSidebar,
};

export function ContextualSidebar() {
  const location = useLocation();
  const activeSection = useActiveSection();

  const section = SECTIONS.find(s => s.id === activeSection);
  // A module switched off takes its entries away (PLAN-GENERICITE § 3.7).
  const off = useSwitchedOffModules();
  const scope = useMemberScope();
  const canOpen = useCanOpen();

  // No sidebar for dashboard or settings (settings has its own layout)
  if (!section || activeSection === 'settings') {
    return null;
  }

  // Custom sidebar component (e.g., NotesSidebar)
  const CustomSidebar = CUSTOM_SIDEBARS[activeSection];
  if (CustomSidebar) {
    return <CustomSidebar />;
  }

  const items = itemsOn(section.sidebarItems, off, scope, canOpen);
  if (items.length === 0) {
    return null;
  }

  const isItemActive = (href: string) => {
    if (location.pathname === href) return true;
    if (href === '/admin') return false;
    // Only match sub-routes (href + "/...")
    return location.pathname.startsWith(href + '/');
  };

  return (
    <aside className="w-52 h-full bg-white border-r border-neutral-200 flex-shrink-0 flex flex-col">
      {/* Header — aligned with app header */}
      <div className="h-14 flex items-center px-5 border-b border-neutral-200 flex-shrink-0">
        <section.icon className="h-4 w-4 mr-2 text-[#0066FF]" />
        <span className="text-xs font-semibold uppercase tracking-wider text-[#0066FF]">
          {section.label}
        </span>
      </div>

      {/* Items */}
      <div className="px-3 py-3 flex-1 overflow-y-auto">
        <nav className="space-y-0.5">
          {items.map((item) => {
            const isActive = isItemActive(item.href);
            const ItemIcon = item.icon;

            return (
              <React.Fragment key={item.href}>
                {item.dividerBefore && (
                  <div className="border-t border-neutral-100 my-2" />
                )}
                <Link
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-[#0066FF] to-[#00AAFF] text-white shadow-md shadow-blue-500/20'
                      : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
                  }`}
                >
                  <ItemIcon
                    className={`flex-shrink-0 mr-3 h-4 w-4 ${
                      isActive ? 'text-white' : 'text-neutral-400 group-hover:text-neutral-500'
                    }`}
                  />
                  <span className="truncate">{item.name}</span>
                </Link>
              </React.Fragment>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
